import { Button, ButtonSize } from '@atlas/components';
import classNames from 'classnames';
import type { FC } from 'react';

import Icon from 'components/Icon/Icon';
import Link from 'components/Link';
import type { Config, GetFieldType } from 'config/types';
import { getMediaUrl } from 'utils/assets';
import pushToDataLayer, { btag } from 'utils/dataLayer';
import useLayout, { type LayoutType } from 'utils/layout';
import { Translation } from 'utils/translations';

import styles from './GetStarted.module.scss';

type GetStartedProps = Omit<
  Extract<
    GetFieldType<Config, 'config.siteConfig.homePage.getStarted'>,
    { enabled: true }
  >,
  'enabled'
>;

const defaultIcons = [
  'user-octagon.bold',
  'wallet-check.bold',
  'blackjack.bold',
];

const ICON_SIZE: Partial<Record<LayoutType | 'default', number>> = {
  wide: 48,
  default: 40,
};

const GetStarted: FC<GetStartedProps> = ({ title, items, actionButton }) => {
  const layout = useLayout();
  const iconSize = ICON_SIZE[layout] || ICON_SIZE.default;

  return (
    <div className={classNames(styles.wrapper, styles[layout])}>
      <h3 className={styles.title}>
        <Translation message={title} />
      </h3>
      <div className={styles.items}>
        {items.slice(0, 3).map(({ label, description, icon }, i) => (
          <div key={label} className={styles.item}>
            <div className={styles.index}>0{i + 1}</div>
            <div className={styles.content}>
              <h4>
                <Translation message={label} />
              </h4>
              <p>
                <Translation message={description} />
              </p>
            </div>
            <div className={styles['icon-wrapper']}>
              {icon ? (
                // biome-ignore lint/a11y/useAltText: This is a decorative image
                <img
                  src={getMediaUrl(icon, 'cms')}
                  width={iconSize}
                  height={iconSize}
                  className={styles.icon}
                />
              ) : (
                <div className={styles.icon}>
                  <Icon
                    height={iconSize}
                    width={iconSize}
                    name={defaultIcons[i]}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <Link
        to="/signup"
        className={styles['action-button']}
        onClick={() => pushToDataLayer('main_create_account_click', btag)}
      >
        <Button size={ButtonSize.big}>
          <Translation message={actionButton} />
        </Button>
      </Link>
    </div>
  );
};

export default GetStarted;
